import type { SavedView, SavedViewMode } from '../../../data/client';

/**
 * THE URL CONTRACT (D19).
 *
 *   /objects/:objectId                    the default view, or an unsaved table
 *   /objects/:objectId?view=<uuid>        that saved view
 *   /objects/:objectId?view=<uuid>&mode=board   the same view, shown as a board
 *   /objects/:objectId?view=table         LEGACY, still works
 *   /objects/:objectId?view=board         LEGACY, still works
 *
 * THE LEGACY LITERALS ARE NOT COURTESY. `packages/e2e/specs/board.spec.ts` navigates to
 * `?view=board` and must keep passing unchanged, and every link anybody has already
 * copied out of the address bar is one of these two. They mean what they always meant:
 * an unsaved list in that mode.
 *
 * THE VIEW OWNS THE MODE; `&mode=` IS AN OVERRIDE. A view saved as a board opens as a
 * board with no query string at all, which is what makes "Leads opens as a board by
 * stage" (D22) a property of the view rather than of the link somebody sent.
 */
export const LEGACY_VIEW_LITERALS = ['table', 'board'] as const;

export interface ViewSelection {
  /** A saved view's id, or null for an unsaved list. */
  readonly viewId: string | null;
  /** An explicit `&mode=` (or a legacy literal), or null for "whatever the view says". */
  readonly modeOverride: SavedViewMode | null;
  /**
   * THE URL SAID "no view", rather than saying nothing at all.
   *
   * A bare URL opens on the object's DEFAULT view (D22). A legacy `?view=board` must NOT:
   * it has meant "this object's board, unfiltered" since WP4, and silently resolving it
   * to a default view with a filter would narrow every link anybody has already sent.
   * `viewId: null` alone cannot tell the two apart, which is what this flag is for.
   */
  readonly explicit: boolean;
}

export function readSelection(params: URLSearchParams): ViewSelection {
  const view = params.get('view');
  const mode = asMode(params.get('mode'));

  if (view === null || view === '') return { viewId: null, modeOverride: mode, explicit: false };
  const legacy = asMode(view);
  // `?view=board` is a MODE, not a view id. Treating it as an id would 404 a link that
  // has worked since WP4.
  if (legacy !== null) return { viewId: null, modeOverride: mode ?? legacy, explicit: true };
  return { viewId: view, modeOverride: mode, explicit: false };
}

function asMode(value: string | null): SavedViewMode | null {
  return value === 'table' || value === 'board' ? value : null;
}

/**
 * The next query string, MERGED over what is already there.
 *
 * `setSearchParams({ view: next })` replaces the WHOLE search string, which is how the
 * mode toggle used to wipe everything beside it - and would have wiped a `?view=<uuid>`
 * the moment one existed. Everything this function does not name is carried through
 * untouched, so a parameter another feature adds later cannot be deleted by a click on
 * Board.
 */
export function writeSelection(
  current: URLSearchParams,
  selection: ViewSelection,
): URLSearchParams {
  const next = new URLSearchParams(current);

  if (selection.viewId === null) next.delete('view');
  else next.set('view', selection.viewId);

  if (selection.modeOverride === null) next.delete('mode');
  else next.set('mode', selection.modeOverride);

  /**
   * An unsaved list in a non-default mode still has to be linkable, and the legacy form
   * is the one every existing link uses - so `?view=board` is what it writes rather than
   * `?mode=board`. One shape on the wire, and `board.spec.ts` keeps passing.
   */
  if (selection.viewId === null && selection.modeOverride !== null) {
    next.delete('mode');
    next.set('view', selection.modeOverride);
  }
  return next;
}

/**
 * Which view a bare URL opens on: the object's default, or nothing.
 *
 * D22's seeded defaults are what make this worth having - "Payments opens on what is due
 * this week" is this line plus a seeded row. At most one view per object can be the
 * default (a partial unique index says so), so `find` is exact rather than a first-wins
 * guess.
 */
export function defaultViewOf(views: readonly SavedView[]): SavedView | undefined {
  return views.find((view) => view.isDefault);
}

/** The selected view, or the default, or none - in that order. */
export function resolveView(
  views: readonly SavedView[],
  selection: ViewSelection,
): SavedView | undefined {
  // "No view" was ASKED FOR (a legacy literal, or the switcher's "All records"), so the
  // default must not quietly take over.
  if (selection.explicit) return undefined;
  const viewId = selection.viewId;
  if (viewId === null) return defaultViewOf(views);
  /**
   * A view id that no longer resolves falls back to the DEFAULT rather than to nothing.
   * The link is stale - somebody deleted the view - and the useful thing to do with a
   * stale link is to open the list it was about, not an empty screen.
   */
  return views.find((view) => view.id === viewId) ?? defaultViewOf(views);
}
